import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TaskSyncBanner } from "./TaskSyncBanner";
import { supabase } from "../../supabase";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITY } from "../../helpers/permissions";
import { isOffline } from "../../helpers/connectivity";
import { blockDemoSave } from "../../helpers/guestMode";
import { loadUsersByAuthIdsForOrg } from "../../helpers/scopedQueries";
import {
  createProjectTask,
  deleteProjectTask,
  loadProjectTasks,
  saveTaskOrder,
  setProjectTaskStatusOfflineAware,
  updateProjectTask,
} from "../../helpers/tasks/projectTasks";
import {
  countTasks,
  featureFilterOptions,
  filterTasks,
  GENERAL_LOCATION,
  groupTasksByLocation,
  planTaskMove,
  taskMetaLabel,
  TASK_STATUS,
} from "../../helpers/tasks/taskModel";
import { RecBar, RecIcon } from "../materials/MaterialRecordParts";
import { TaskBuilderFlow } from "./taskBuilder/TaskBuilderFlow";
import { TaskDetails } from "./TaskDetails";
import { TaskEditor } from "./TaskEditor";
import { ProjectTasksDesktop } from "./ProjectTasksDesktop";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { useWorkSchedule } from "../../hooks/useWorkSchedule";
import { DueChip, DurationChip, initialsOf, PriorityChip, ProgressChip, TaskCheck } from "./TaskParts";
import "./tasks.css";

function TaskRow({ task, assigneeName, location, canComplete, busy, menuItems, onOpen, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === "keydown") {
        if (event.key === "Escape") setOpen(false);
        return;
      }
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const done = task.status === TASK_STATUS.COMPLETED;
  const { minutesPerDay } = useWorkSchedule();
  const meta = taskMetaLabel(task, { minutesPerDay });
  return (
    <div className={`tk-row${done ? " is-done" : ""}`} data-testid="project-task-row">
      <TaskCheck task={task} interactive={canComplete} busy={busy} onToggle={onToggle} />
      <button type="button" className="tk-row__main" onClick={() => onOpen(task)} aria-label={`Open task ${task.title}`}>
        <span className="tk-row__title">{task.title}</span>
        <span className="tk-row__meta">
          {done ? (
            <span className="tk-row__done">{meta}</span>
          ) : (
            <>
              <PriorityChip priority={task.priority} />
              <DueChip task={task} />
              <DurationChip minutes={task.planned_duration_minutes} />
              <ProgressChip task={task} />
            </>
          )}
          {location ? <span className="tk-row__loc">{location}</span> : null}
        </span>
      </button>
      {assigneeName ? (
        <span className="tk-avatar" title={assigneeName} aria-label={`Assigned to ${assigneeName}`}>
          {initialsOf(assigneeName)}
        </span>
      ) : null}
      {menuItems.length ? (
        <div className="tk-menuwrap" ref={ref}>
          <button
            type="button"
            className="tk-iconbtn"
            aria-label={`Actions for ${task.title}`}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <RecIcon name="more" className="tk-more" />
          </button>
          {open ? (
            <div className="mat-menu tk-menu" role="menu">
              {menuItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className={`mat-menu__item${item.danger ? " tk-danger" : ""}`}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect(task);
                  }}
                >
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Project Tasks (and Location Tasks: pass `scopeLocation`). One screen, four
 * views: list → details → edit / new. All data goes through
 * helpers/tasks/projectTasks.js; this component only presents it.
 */
export function ProjectTasksScreen({
  projectId,
  projectName,
  locations = [],
  scopeLocation = null,
  writable = true,
  onBack,
  onChanged,
  initialTaskId = null,
  onDeepLinkReturn,
  callerBackLabel = "",
  onOpenProjectTasks,
}) {
  const isDesktopViewport = useIsDesktopViewport();
  const { can, actor, directReportIds } = usePermissions();
  const canManage = can(CAPABILITY.PROJECT_TASK_MANAGE) && writable;
  const canComplete = can(CAPABILITY.PROJECT_TASK_COMPLETE) && writable;
  const canSaveToCatalog = can(CAPABILITY.TASK_CATALOG_EDIT);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fromDevice, setFromDevice] = useState(false);
  const [hasDeviceCopy, setHasDeviceCopy] = useState(true);
  const [names, setNames] = useState({});
  const [busyId, setBusyId] = useState("");
  const [view, setView] = useState({ type: "list" });
  const openedInitialTask = useRef(false);
  const fromDeepLink = useRef(false);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilterState] = useState(scopeLocation?.locationId || "");
  const [featureFilter, setFeatureFilter] = useState("");
  // Changing or clearing the Location clears a Feature that belonged to it.
  const setLocationFilter = useCallback((value) => {
    setLocationFilterState(value);
    setFeatureFilter("");
  }, []);

  const offline = fromDevice || isOffline();
  const mutable = (can(CAPABILITY.PROJECT_TASK_MANAGE) || canComplete) && !offline;
  // C8: complete / reopen also works offline from the device copy; the change is queued and synced later.
  const canToggle = canComplete && (!offline || hasDeviceCopy);

  const reload = useCallback(async () => {
    setError("");
    try {
      const result = await loadProjectTasks(actor, projectId);
      setTasks(result.tasks);
      setFromDevice(Boolean(result.fromDevice));
      setHasDeviceCopy(result.hasDeviceCopy !== false);
      onChanged?.(result.tasks);
    } catch (err) {
      setError(err?.message || "Unable to load tasks.");
    } finally {
      setLoading(false);
    }
  }, [actor, projectId, onChanged]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, actor?.supabase_user_id]);

  // Deep link (e.g. from Planning): open the exact task once it has loaded.
  useEffect(() => {
    if (!initialTaskId || openedInitialTask.current || loading) return;
    openedInitialTask.current = true;
    const match = tasks.find((t) => t.project_task_id === initialTaskId);
    if (match) {
      fromDeepLink.current = true;
      setView({ type: "details", task: match });
    }
  }, [initialTaskId, loading, tasks]);

  useEffect(() => {
    const ids = [...new Set(tasks.map((t) => t.assignee_id).filter(Boolean))];
    if (!ids.length || isOffline()) return;
    let cancelled = false;
    loadUsersByAuthIdsForOrg(supabase, actor, ids, "supabase_user_id, employee_name")
      .then((users) => {
        if (cancelled) return;
        setNames((current) => ({
          ...current,
          ...Object.fromEntries((users || []).map((u) => [u.supabase_user_id, u.employee_name])),
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const featureName = useMemo(
    () =>
      Object.fromEntries(
        locations.flatMap((l) => (l.features || []).map((f) => [f.project_feature_id, f.name])),
      ),
    [locations],
  );
  const locationName = useMemo(
    () => Object.fromEntries(locations.map((l) => [l.project_location_id, l.name])),
    [locations],
  );

  const effectiveLocation = scopeLocation?.locationId || locationFilter;
  const featureOptions = useMemo(() => featureFilterOptions(locations, effectiveLocation), [locations, effectiveLocation]);
  const activeFeature = featureOptions.some((f) => f.id === featureFilter) ? featureFilter : "";
  const scoped = useMemo(
    () => filterTasks(tasks, { locationId: effectiveLocation, featureId: activeFeature, search }),
    [tasks, effectiveLocation, activeFeature, search],
  );
  const counts = countTasks(scoped);
  const visible = filterTasks(scoped, { status });
  const groups = useMemo(() => groupTasksByLocation(visible, locations), [visible, locations]);
  const showGroupHeaders = !scopeLocation && (locations.length > 0 || groups.length > 1);

  async function run(taskId, work) {
    if (blockDemoSave(actor)) return;
    setBusyId(taskId || "x");
    setError("");
    try {
      await work();
      await reload();
    } catch (err) {
      setError(err?.message || "Unable to save the change.");
    } finally {
      setBusyId("");
    }
  }

  const toggle = (task) =>
    run(task.project_task_id, async () => {
      const next = task.status === TASK_STATUS.COMPLETED ? TASK_STATUS.OPEN : TASK_STATUS.COMPLETED;
      const updated = await setProjectTaskStatusOfflineAware(actor, task, next);
      setView((v) => (v.task?.project_task_id === task.project_task_id ? { ...v, task: updated } : v));
    });

  const remove = (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    return run(task.project_task_id, async () => {
      await deleteProjectTask(actor, task.project_task_id);
      setView({ type: "list" });
    });
  };

  const move = (task, direction) =>
    run(task.project_task_id, () =>
      saveTaskOrder(actor, projectId, planTaskMove(tasks, task.project_task_id, direction)),
    );

  const menuFor = (task) => [
    { key: "view", label: "View details", onSelect: () => setView({ type: "details", task }) },
    ...(canManage && !offline
      ? [
          { key: "edit", label: "Edit", onSelect: () => setView({ type: "edit", task }) },
          { key: "up", label: "Move up", onSelect: (t) => move(t, "up") },
          { key: "down", label: "Move down", onSelect: (t) => move(t, "down") },
          { key: "delete", label: "Delete", danger: true, onSelect: remove },
        ]
      : []),
  ];

  const titleText = scopeLocation ? `${scopeLocation.name} Tasks` : "Project Tasks";

  if (view.type === "details") {
    const task = tasks.find((t) => t.project_task_id === view.task.project_task_id) || view.task;
    const leavingCaller = Boolean(fromDeepLink.current && onDeepLinkReturn);
    const projectDestinationId = task.project_id || projectId || "";
    const canOpenProject =
      Boolean(projectDestinationId) &&
      (projectDestinationId === projectId || typeof onOpenProjectTasks === "function");
    return (
      <TaskDetails
        task={task}
        projectId={projectId}
        projectName={projectName}
        locationName={task.location_id ? locationName[task.location_id] : ""}
        featureName={task.feature_id ? featureName[task.feature_id] || "Feature" : ""}
        assigneeName={names[task.assignee_id] || ""}
        canEdit={canManage && !offline}
        canComplete={canToggle}
        busy={Boolean(busyId)}
        error={error}
        backLabel={leavingCaller ? callerBackLabel : titleText}
        onOpenProject={
          canOpenProject
            ? (destinationId) => {
                if (!destinationId) return;
                if (destinationId !== projectId) {
                  onOpenProjectTasks?.(destinationId);
                  return;
                }
                fromDeepLink.current = false;
                setView({ type: "list" });
              }
            : null
        }
        onBack={() => {
          // Entered from Planning: Back returns there; any other route keeps the task list.
          if (fromDeepLink.current && onDeepLinkReturn) {
            fromDeepLink.current = false;
            onDeepLinkReturn();
            return;
          }
          setView({ type: "list" });
        }}
        onEdit={() => setView({ type: "edit", task })}
        onToggle={() => toggle(task)}
        onDelete={() => remove(task)}
      />
    );
  }

  if (view.type === "ashley") {
    return (
      <TaskBuilderFlow
        actor={actor}
        projectId={projectId}
        projectName={projectName}
        locations={locations}
        directReportIds={directReportIds}
        onBack={() => setView({ type: "list" })}
        onTasksCreated={(count) => {
          // reload() reports the saved rows to the parent. An empty
          // onChanged() would set that list to undefined and crash the
          // location screen on the next render, after the inserts succeed.
          reload();
          if (count) setError("");
        }}
      />
    );
  }

  if (view.type === "edit" || view.type === "new") {
    return (
      <TaskEditor
        task={view.type === "edit" ? view.task : null}
        projectName={projectName}
        locations={locations}
        defaultLocationId={scopeLocation?.locationId || (locationFilter !== GENERAL_LOCATION ? locationFilter : "")}
        canSaveToCatalog={canSaveToCatalog}
        actor={actor}
        directReportIds={directReportIds}
        onCancel={() => setView(view.type === "edit" ? { type: "details", task: view.task } : { type: "list" })}
        onSubmit={async (input) => {
          if (blockDemoSave(actor)) throw new Error("Demo Mode — Sign in to save your work.");
          if (view.type === "edit") {
            const updated = await updateProjectTask(actor, view.task.project_task_id, {
              title: input.title,
              description: input.description,
              location_id: input.locationId || null,
              feature_id: input.featureId || null,
              assignee_id: input.assigneeId || null,
              priority: input.priority || null,
              due_date: input.dueDate || null,
              start_date: input.startDate || null,
              planned_duration_minutes: input.durationMinutes ?? null,
            });
            await reload();
            setView({ type: "details", task: updated });
          } else {
            await createProjectTask(actor, projectId, input);
            await reload();
            setView({ type: "list" });
          }
        }}
      />
    );
  }

  const tabs = [
    ["all", "All", counts.total],
    [TASK_STATUS.OPEN, "Open", counts.open],
    [TASK_STATUS.COMPLETED, "Completed", counts.completed],
  ];

  if (isDesktopViewport) {
    return (
      <ProjectTasksDesktop
        title={titleText}
        projectName={projectName}
        onBack={onBack}
        tasks={tasks}
        groups={groups}
        counts={counts}
        loading={loading}
        offline={offline}
        syncBanner={<TaskSyncBanner actor={actor} projectId={projectId} />}
        hasDeviceCopy={hasDeviceCopy}
        error={error}
        status={status}
        onStatus={setStatus}
        search={search}
        onSearch={setSearch}
        locations={locations}
        scopeLocation={scopeLocation}
        locationFilter={locationFilter}
        onLocationFilter={setLocationFilter}
        featureOptions={featureOptions}
        featureFilter={activeFeature}
        onFeatureFilter={setFeatureFilter}
        showLocationFilter={locations.length > 0 || tasks.some((t) => !t.location_id)}
        showGroupHeaders={showGroupHeaders}
        canManage={canManage}
        canComplete={canComplete}
        mutable={mutable || (offline && canToggle)}
        busyId={busyId}
        names={names}
        featureName={featureName}
        menuFor={menuFor}
        onOpen={(t) => setView({ type: "details", task: t })}
        onToggle={toggle}
        onNew={() => setView({ type: "new" })}
        onAshley={() => setView({ type: "ashley" })}
      />
    );
  }

  return (
    <div className="screen nx-screen rec-page" data-testid="project-tasks">
      <RecBar title={titleText} onBack={onBack} trailing={null} />
      {projectName ? <div className="tk-subtitle">{projectName}</div> : null}

      <div className="rec-body">
        <TaskSyncBanner actor={actor} projectId={projectId} />
        {offline ? (
          <div className="tk-notice" role="status" data-testid="tasks-offline-notice">
            {hasDeviceCopy
              ? "Showing tasks saved on this device. You can complete or reopen tasks; changes sync when you reconnect."
              : "Tasks for this project are not saved on this device yet. Open them once while online."}
          </div>
        ) : null}
        {error ? (
          <div className="rec-banner" role="alert">
            {error}
          </div>
        ) : null}

        <div className="mat-searchbox">
          <RecIcon name="search" />
          <input
            type="search"
            className="mat-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks…"
            aria-label="Search tasks"
            autoComplete="off"
          />
        </div>

        <div className="tl-scope" role="tablist" aria-label="Task status">
          {tabs.map(([value, label, count]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={status === value}
              className={`tl-scope__tab${status === value ? " is-on" : ""}`}
              onClick={() => setStatus(value)}
            >
              {label} <span className="tk-count">{count}</span>
            </button>
          ))}
        </div>

        {!scopeLocation && (locations.length > 0 || tasks.some((t) => !t.location_id)) ? (
          <span className="rec-select tk-locfilter">
            <select
              className="rec-input"
              aria-label="Location filter"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="">All Locations</option>
              {locations.map((l) => (
                <option key={l.project_location_id} value={l.project_location_id}>
                  {l.name}
                </option>
              ))}
              <option value={GENERAL_LOCATION}>General (whole project)</option>
            </select>
          </span>
        ) : null}
        {featureOptions.length ? (
          <span className="rec-select tk-locfilter tk-featfilter">
            <select
              className="rec-input"
              aria-label="Feature filter"
              value={activeFeature}
              onChange={(e) => setFeatureFilter(e.target.value)}
            >
              <option value="">All Features</option>
              {featureOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </span>
        ) : null}

        {canManage && !offline ? (
          <div className="tk-newrow">
            <button
              type="button"
              className="tk-newbtn"
              aria-label="+ New Task"
              onClick={() => setView({ type: "new" })}
            >
              <RecIcon name="plus" />
              New Task
            </button>
            <button
              type="button"
              className="tk-newbtn tk-newbtn--ashley"
              aria-label="Build Tasks with Ashley"
              onClick={() => setView({ type: "ashley" })}
            >
              <span aria-hidden="true">✨</span>
              Build Tasks with Ashley
            </button>
          </div>
        ) : null}

        {loading ? (
          <p className="pj-empty">Loading tasks…</p>
        ) : groups.length === 0 ? (
          <div className="pj-empty" data-testid="tasks-empty">
            {tasks.length === 0
              ? "No tasks yet."
              : "No tasks match these filters."}
            {tasks.length === 0 && canManage && !offline ? (
              <div>Add the first one with New Task.</div>
            ) : null}
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.locationId || "general"} className="tk-group" data-testid="task-group">
              {showGroupHeaders ? (
                <div className="tk-group__head">
                  <span className="tk-group__name">{group.label}</span>
                  <span className="tk-group__sub">
                    {group.total} {group.total === 1 ? "task" : "tasks"} · {group.open} open
                  </span>
                </div>
              ) : null}
              <div className="tk-list">
                {group.tasks.map((task) => (
                  <TaskRow
                    key={task.project_task_id}
                    task={task}
                    assigneeName={names[task.assignee_id] || ""}
                    location={task.feature_id ? featureName[task.feature_id] || "Feature" : null}
                    canComplete={canToggle}
                    busy={busyId === task.project_task_id}
                    menuItems={menuFor(task)}
                    onOpen={(t) => setView({ type: "details", task: t })}
                    onToggle={toggle}
                  />
                ))}
              </div>
            </section>
          ))
        )}
        <div className="pj-pagepad" aria-hidden="true" />
      </div>
    </div>
  );
}
